import {SubmissionError} from 'redux-form';
import flatten from 'lodash/flatten';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';

import http from '../../../../shared/http';
import geoToLang from '../../../../../../../shared/js/geo_landing_lang_relations';
import endpoints from '../../../../../config/endpoints';
import { actions as parentActions } from '../../../editor';
import {
	createSelectOptionsFromSysEnums,
	createSelectValuesUseIntersection
} from '../../../../shared/form-elements/select';
import {
	createSelectOptionsFromSysEnums as createSelectOptionsFromSysEnumsCountries
} from '../../../../shared/form-elements/select-countries';

const defaultInitialState = {
	currentLanguage: 'en',
	fields: {
		id: undefined,
		domain: '',
		title: '',
		display_priority: 1,
		affiliate_ids: [],
		labels: [],
		languages: null,
		is_restricted: false,
		is_mt_approved: false,
		default_country_id: ''
	},
	isShowDeleteModal: false,
	mustTranslateFields: []
};

const createGeneralForm = ({
	namespace,
	stateNodeName
}) => {
	// action types
	const LANDINGS_FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const LANDINGS_FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const DELETE_REQUEST_SUCCESS = `${namespace}/${stateNodeName}-delete-request-success`;
	const DELETE_REQUEST_ERROR = `${namespace}/${stateNodeName}-delete-request-errror`;
	const DELETE_MODAL= `${namespace}/${stateNodeName}-delete-show-modal`;

	const LANDINGS_FORM_CHANGE_LANGUAGE = `${namespace}/${stateNodeName}-change-language`;
	const LANDINGS_FORM_TRANSLATION_REQUEST_SUCCESS = `${namespace}/${stateNodeName}-translation-request-success`;
	const LANDINGS_FORM_TRANSLATION_REQUEST_ERROR = `${namespace}/${stateNodeName}-translation-request-error`;

	const AFFILIATES_LOOKUP_SUCCESS = `${namespace}/${stateNodeName}-affiliates-lookup-success`;
	const AFFILIATES_LOOKUP_ERROR = `${namespace}/${stateNodeName}-affiliates-lookup-error`;

	// action types
	const FETCH_SUGGEST_SUCCESS = `${namespace}/fetch-suggest-success`;
	const FETCH_SUGGEST_ERROR = `${namespace}/fetch-suggest-error`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		landingsFormSubmit: formData => (dispatch, getState) => {
			const { landingsForm, offer } = getState().programsEditor;

			const flattenFormData = formData && flatten(
				Object.keys(formData).map(type => {
					return formData[type].map((dataObject, index) => {
						const displayPriority = (formData[type].length - 1) - index;
						return {
							...landingsForm.fields,
							...dataObject,
							affiliate_ids: dataObject['affiliate_ids'] &&
								dataObject['affiliate_ids'].map(row => row.value) || [],
							default_country_id: get(dataObject, 'default_country_id.0.value', null),
							labels: dataObject['labels'] &&
								dataObject['labels'].map(row => row.value) || [],
							type,
							display_priority: displayPriority
						};
					});
				})
			) || {};

			const request = http({
				url: endpoints.OFFERS.LANDINGS.replace('{id}', offer.id),
				method: 'put',
				actions: [
					LANDINGS_FORM_SUBMIT_SUCCESS,
					LANDINGS_FORM_SUBMIT_ERROR
				],
				data: flattenFormData
			});

			return request(dispatch).then(action => {

				if (action.type === LANDINGS_FORM_SUBMIT_SUCCESS) {
					dispatch(parentActions.toggleProgressBar());
					dispatch(parentActions.fetchLandingsData(offer.id));
				}

				if (action.type === LANDINGS_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					const validationsErrors = prepareFormErrors(formData, responseErrors);
					throw new SubmissionError(validationsErrors);
				}
			});
		},

		landingsDelete: (id, substitution) => (dispatch, getState) => {
			const { offer } = getState().programsEditor;

			const request = http({
				url: `${endpoints.OFFERS.LANDINGS_DELETE.replace('{id}', id)}${`?substitution=${substitution}`}`,
				method: 'delete',
				actions: [
					DELETE_REQUEST_SUCCESS,
					DELETE_REQUEST_ERROR
				]
			});

			request(dispatch).then(action => {
				if (action.type === DELETE_REQUEST_SUCCESS) {
					dispatch(parentActions.toggleProgressBar());
					dispatch(parentActions.fetchLandingsData(offer.id));
				}
			});
		},

		showDeleteModal: (data) => (dispatch) => {
			return dispatch({
				type: DELETE_MODAL,
				payload: data
			});
		},

		landingsFormChangeLanguage: lang => (dispatch, getState) => {
			const { defaultLanguage, offer } = getState().programsEditor;
			const actionChangeLang = {type: LANDINGS_FORM_CHANGE_LANGUAGE, payload: lang};
			const request = actions.translationRequest(offer.id, lang);

			if (lang === defaultLanguage) return dispatch(actionChangeLang);

			dispatch(parentActions.toggleProgressBar());

			dispatch(request).then(action => {
				if (action.type === LANDINGS_FORM_TRANSLATION_REQUEST_SUCCESS) {
					dispatch({
						...actionChangeLang,
						meta: action.payload.data
					});
				}
			});
		},

		landingsFormSubmitTranslation: formData => (dispatch, getState) => {
			const { landingsForm: { currentLanguage }, offer } = getState().programsEditor;

			const flattenFormData = flatten(
				Object.keys(formData).map(type => {
					return formData[type].map(filed => ({
						'offer_domain_id': filed.id,
						language: currentLanguage,
						title: filed.title
					}));
				})
			);

			const request = actions.translationRequest(offer.id, currentLanguage, 'put', flattenFormData);

			return dispatch(request).then(action => {
				if (action.type === LANDINGS_FORM_TRANSLATION_REQUEST_SUCCESS) {
					dispatch(actions.landingsFormChangeLanguage(currentLanguage));
				}

				if (action.type === LANDINGS_FORM_TRANSLATION_REQUEST_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					const validationsErrors = prepareFormErrors(formData, responseErrors);
					throw new SubmissionError(validationsErrors);
				}
			});

		},

		locationsLookupsLandings: text => (dispatch, getState) => {
			const offer_id = getState().programsEditor.data.id;
			const request = http({
				url: `${endpoints.LOOKUPS.OFFER_DEFAULT_GEO}?query=${text}&offer_id=${offer_id}`,
				method: 'get',
				actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnumsCountries( action.payload.data )
					};
				}
			});
		},

		translationRequest: (id, lang, method = 'get', data = {}) => dispatch => {
			const url = endpoints.OFFERS.LANDINGS_TRANSLATION.replace('{id}', id).replace('{lang}', lang);
			return (http({
				url,
				method,
				actions: [
					LANDINGS_FORM_TRANSLATION_REQUEST_SUCCESS,
					LANDINGS_FORM_TRANSLATION_REQUEST_ERROR
				],
				data
			}))(dispatch);
		},

		affiliatesLookup: text => dispatch => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [
					AFFILIATES_LOOKUP_SUCCESS,
					AFFILIATES_LOOKUP_ERROR
				]
			});

			return request(dispatch).then(action => {
				if (action.type === AFFILIATES_LOOKUP_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		}
	};

	// reducers
	const reducers = {
		[LANDINGS_FORM_CHANGE_LANGUAGE]: (state, action) => {
			const { payload, meta } = action;

			return {
				...state,
				showProgressBar: false,
				landingsForm: {
					...state.landingsForm,
					currentLanguage: payload,
					mustTranslateFields: meta ? meta : []
				}
			};
		},
		[LANDINGS_FORM_SUBMIT_ERROR]: errorReducerFn,
		[LANDINGS_FORM_TRANSLATION_REQUEST_ERROR]: errorReducerFn,
		[DELETE_REQUEST_ERROR]: errorReducerFn,
		[DELETE_MODAL]: (state, action) => {
			return {
				...state,
				deleteLandingModal: {
					show: !state.deleteLandingModal.show,
					data: action.payload
				}
			};
		},
		[AFFILIATES_LOOKUP_ERROR]: errorReducerFn
	};

	// selectors
	const selectors = {
		getLandingsFormData: state => {
			const {
				landings,
				landingsForm: { currentLanguage, mustTranslateFields },
				defaultLanguage
			} = state.programsEditor;
			const domainTypes = state.root.lists['offer-domain-types'];
			const targeting = state.programsEditor.targeting || [];
			const targetingBasedLanguages = [].concat(...targeting.map(trgt => geoToLang[trgt.country_code]));

			const languagesOptions = state.root.lists['landing-languages']
				.filter(lang => ['en', ...targetingBasedLanguages].includes(lang.id));

			const shouldTranslate = defaultLanguage !== currentLanguage;
			const offerPromoLabelsOptions = createSelectOptionsFromSysEnums(state.root.lists['offer-promo-labels']);

			let initialValues = {};

			landings && domainTypes.forEach(type => {
				const filtered = (
					landings
						.filter(entity => entity.type === type.id)
						.map(entity => {
							const cloneEntity = cloneDeep(entity);
							if (shouldTranslate) {
								const inTranslateFields = find(mustTranslateFields, { 'offer_domain_id': cloneEntity.id });
								cloneEntity.title = inTranslateFields ? inTranslateFields.title : '';
							}
							return {
								...cloneEntity,
								affiliate_ids: cloneEntity['affiliates'] &&
									cloneEntity['affiliates'].map(row => ({
										value: row.id,
										label: row.email
									})) || [],
								default_country_id: cloneEntity.default_country_id && [
									{
										value: cloneEntity.default_country_id,
										label: cloneEntity.default_country_name
									}
								],
								labels: createSelectValuesUseIntersection(
									offerPromoLabelsOptions,
									cloneEntity['labels']
								)
							};
						})
				);

				if (filtered) {
					initialValues[type.id] = orderBy(filtered, ['display_priority'], ['desc']);
				}
			});

			return {
				shouldTranslate,
				initialValues,
				defaultLanguage,
				languagesOptions,
				offerPromoLabelsOptions
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const prepareFormErrors = (formData, responseErrors) => {
	const formFieldsFlatten = [];

	Object.keys(formData).forEach(domainType => {
		formData[domainType].forEach(v => {
			formFieldsFlatten.push(domainType);
		});
	});

	const fieldsWithError = formFieldsFlatten.map((domainType, index) => {
		const fieldErrors = {};

		responseErrors.filter(error => {
			const indexForParse = error.dataPath.split('.')[0];
			return parseInt(indexForParse.replace('[', '').replace(']', '')) === index;
		}).forEach(error => {
			const fieldName = error.dataPath.split('.')[1];
			fieldErrors[fieldName] = error.message;
		});

		return {
			fieldErrors,
			domainType
		};

	});

	return mapValues(
		groupBy(fieldsWithError, error => error.domainType),
		error => error.map(entity => entity.fieldErrors)
	);
};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return ({ ...state, error: true, showProgressBar: false });
	}
	return state;
};

export default createGeneralForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/components/forms/landings/index.js